import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Pagination } from './ui/pagination';
import { 
  Loader2,
  Search,
  Plus,
  Edit,
  Eye,
  Trash2,
  RefreshCw,
  Building2,
  MapPin,
  Phone,
  Mail,
  User,
  Package
} from 'lucide-react';
import { storeApiService, Store, StoreCreateRequest, StoreUpdateRequest } from '../services/storeApi';
import { useBranch } from '../contexts/BranchContext';
import { toast } from 'sonner';

export function StoreMasterPage() {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch stores
  const fetchStores = useCallback(async () => {
    try {
      setLoading(true);
      const branchId = selectedBranch;
      if (!branchId) {
        // console.warn('User branch information not available');
        setStores([]);
        setTotalCount(0);
        setTotalPages(0);
        return;
      }
      const allStores = await storeApiService.getStoresByBranch(branchId);
      
      // Apply filters on the frontend
      let filteredStores = allStores;
      
      if (debouncedSearchTerm) {
        filteredStores = filteredStores.filter(store => 
          store.storeName.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          store.storeCode.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
        );
      }
      
      if (typeFilter && typeFilter !== '') {
        filteredStores = filteredStores.filter(store => store.storeType === typeFilter);
      }
      
      if (statusFilter && statusFilter !== '') {
        filteredStores = filteredStores.filter(store => store.status === statusFilter);
      }
      
      // Apply pagination on the frontend
      const totalCount = filteredStores.length;
      const totalPages = Math.ceil(totalCount / pageSize);
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedStores = filteredStores.slice(startIndex, endIndex);
      
      setStores(paginatedStores);
      setTotalCount(totalCount);
      setTotalPages(totalPages);
    } catch (error) {
      // console.error('Error fetching stores:', error);
      toast.error('Failed to fetch stores');
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, currentPage, pageSize, debouncedSearchTerm, typeFilter, statusFilter]);

  useEffect(() => {
    if (selectedBranch) {
      fetchStores();
    }
  }, [fetchStores, selectedBranch]);

  const handleRefresh = () => {
    fetchStores();
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this store?')) {
      try {
        await storeApiService.deleteStore(id.toString());
        await fetchStores();
      } catch (error) {
        // console.error('Error deleting store:', error);
        toast.error('Failed to delete store');
      }
    }
  };

  const handleEdit = (id: number) => {
    navigate(`/stores/edit/${id}`);
  };

  const handleView = (id: number) => {
    navigate(`/stores/view/${id}`);
  };

  const handleCreate = () => {
    navigate('/stores/create');
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      ACTIVE: { color: 'bg-green-100 text-green-800', text: 'Active' },
      INACTIVE: { color: 'bg-gray-100 text-gray-800', text: 'Inactive' },
      MAINTENANCE: { color: 'bg-yellow-100 text-yellow-800', text: 'Maintenance' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.INACTIVE;
    return <Badge className={config.color}>{config.text}</Badge>;
  };

  const getTypeBadge = (type: string) => {
    const typeConfig = {
      RETAIL: { color: 'bg-blue-100 text-blue-800', text: 'Retail' },
      WAREHOUSE: { color: 'bg-purple-100 text-purple-800', text: 'Warehouse' },
      DISTRIBUTION_CENTER: { color: 'bg-orange-100 text-orange-800', text: 'Distribution Center' }
    };
    
    const config = typeConfig[type as keyof typeof typeConfig] || typeConfig.RETAIL;
    return <Badge className={config.color}>{config.text}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
              Store Master
            </h1>
            <p className="text-gray-600 mt-1">Manage your stores and locations</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="border-primary-200 text-primary-700 hover:bg-primary-50"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={handleCreate}
              className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg shadow-primary-200"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Store
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Building2 className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-xl font-semibold">Stores</CardTitle>
                  <p className="text-gray-600 text-sm">
                    {totalCount} {totalCount === 1 ? 'store' : 'stores'} found
                  </p>
                </div>
              </div>
              <Badge className="bg-white/20 text-white border-white/30">
                <Package className="h-3 w-3 mr-1" />
                {totalCount}
              </Badge>
            </div>
          </CardHeader>
          
          <CardContent className="p-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search stores..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-48 border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RETAIL">Retail</SelectItem>
                    <SelectItem value="WAREHOUSE">Warehouse</SelectItem>
                    <SelectItem value="DISTRIBUTION_CENTER">Distribution Center</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-48 border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="INACTIVE">Inactive</SelectItem>
                    <SelectItem value="MAINTENANCE">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Table */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
                <p className="text-gray-500">Loading stores...</p>
              </div>
            ) : stores.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No stores found</h3>
                <p className="text-gray-500 mb-4">Get started by creating your first store.</p>
                <Button
                  onClick={handleCreate}
                  className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Store
                </Button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                        <TableHead className="font-semibold text-gray-800">Store Code</TableHead>
                        <TableHead className="font-semibold text-gray-800">Store Name</TableHead>
                        <TableHead className="font-semibold text-gray-800">Type</TableHead>
                        <TableHead className="font-semibold text-gray-800">Location</TableHead>
                        <TableHead className="font-semibold text-gray-800">Manager</TableHead>
                        <TableHead className="font-semibold text-gray-800">Status</TableHead>
                        <TableHead className="font-semibold text-gray-800">Capacity</TableHead>
                        <TableHead className="font-semibold text-gray-800 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stores.map((store) => (
                        <TableRow key={store.id} className="hover:bg-gray-50/50">
                          <TableCell className="font-medium text-gray-900">
                            {store.storeCode}
                          </TableCell>
                          <TableCell className="text-gray-700">
                            <div>
                              <div className="font-medium">{store.storeName}</div>
                              {store.email && (
                                <div className="text-sm text-gray-500 flex items-center mt-1">
                                  <Mail className="h-3 w-3 mr-1" />
                                  {store.email}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {getTypeBadge(store.storeType)}
                          </TableCell>
                          <TableCell className="text-gray-700">
                            <div className="flex items-center">
                              <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                              <div>
                                {store.location ? (
                                  <div className="max-w-xs truncate" title={store.location}>
                                    {store.location}
                                  </div>
                                ) : (
                                  <span className="text-gray-500">Not specified</span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-700">
                            {store.managerName ? (
                              <div className="flex items-center">
                                <User className="h-4 w-4 text-gray-400 mr-2" />
                                {store.managerName}
                              </div>
                            ) : (
                              <span className="text-gray-500">Not assigned</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(store.status)}
                          </TableCell>
                          <TableCell className="text-gray-700">
                            {store.capacity ? (
                              <span className="font-medium">{store.capacity.toLocaleString()}</span>
                            ) : (
                              <span className="text-gray-500">Not set</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                onClick={() => handleView(store.id)}
                                variant="outline"
                                size="sm"
                                className="border-blue-200 text-blue-700 hover:bg-blue-50"
                                title="View Store"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                onClick={() => handleEdit(store.id)}
                                variant="outline"
                                size="sm"
                                className="border-primary-200 text-primary-700 hover:bg-primary-50"
                                title="Edit Store"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                onClick={() => handleDelete(store.id)}
                                variant="outline"
                                size="sm"
                                className="border-red-200 text-red-700 hover:bg-red-50"
                                title="Delete Store"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center mt-6">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
